/**
 * ContractExceptionsModal — Shows contract analysis exceptions (KAN-195)
 * Displays unmatched, ambiguous, and failed contract analysis attempts.
 * Users can resolve or delete exceptions from this modal.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2, AlertTriangle, FileText, Trash2, Check, ChevronDown, ChevronRight, Link2, XCircle, FileQuestion, Files } from 'lucide-react';
import { clientService } from '../../services/api/clientService';
import ConfirmDialog from '../common/ConfirmDialog';
import '../confirmations/ExceptionsModal.css';

interface ContractExceptionsModalProps {
  clientId: string;
  isOpen: boolean;
  onClose: () => void;
  onRefreshNeeded?: () => void;
}

interface ContractException {
  id: string;
  type: 'unmatched' | 'ambiguous' | 'failure';
  status: 'open' | 'resolved';
  attachmentName: string;
  emailId?: string;
  emailSender?: string;
  emailSubject?: string;
  emailDate?: string;
  attemptedBtns?: string[];
  matchedTradeIds?: string[];
  errorMessage?: string;
  retryCount?: number;
  resolution?: string;
  resolvedAt?: string;
  resolvedBy?: string;
  tradeId?: string;
  createdAt?: string;
}

interface ExceptionsResponse {
  exceptions: ContractException[];
  counts: {
    total: number;
    unmatched: number;
    ambiguous: number;
    failure: number;
  };
}

const ContractExceptionsModal: React.FC<ContractExceptionsModalProps> = ({
  clientId,
  isOpen,
  onClose,
  onRefreshNeeded,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'unmatched' | 'ambiguous' | 'failure'>('unmatched');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exceptions, setExceptions] = useState<ContractException[]>([]);
  const [counts, setCounts] = useState({ total: 0, unmatched: 0, ambiguous: 0, failure: 0 });

  const loadExceptions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await clientService.getContractExceptions(clientId, 'open');
      if (result.success && result.data) {
        const data = result.data as ExceptionsResponse;
        setExceptions(data.exceptions || []);
        setCounts(data.counts || { total: 0, unmatched: 0, ambiguous: 0, failure: 0 });
      } else {
        setError(result.message || t('contractExceptions.loadError', 'Failed to load exceptions'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('contractExceptions.loadError', 'Failed to load exceptions'));
    } finally {
      setLoading(false);
    }
  }, [clientId, t]);

  useEffect(() => {
    if (isOpen && clientId) {
      loadExceptions();
    }
  }, [isOpen, clientId, loadExceptions]);

  const handleResolve = async (exceptionId: string, resolution: string, tradeId?: string) => {
    try {
      const result = await clientService.resolveContractException(clientId, exceptionId, resolution, tradeId);
      if (result.success) {
        setExceptions(prev => prev.filter(e => e.id !== exceptionId));
        setCounts(prev => {
          const exception = exceptions.find(e => e.id === exceptionId);
          const type = exception?.type || 'unmatched';
          return {
            ...prev,
            total: Math.max(0, prev.total - 1),
            [type]: Math.max(0, prev[type] - 1),
          };
        });
        onRefreshNeeded?.();
      }
    } catch {
      // silently fail — user sees no change
    }
  };

  const handleDelete = async (exceptionId: string) => {
    try {
      const result = await clientService.deleteContractException(clientId, exceptionId);
      if (result.success) {
        setExceptions(prev => prev.filter(e => e.id !== exceptionId));
        setCounts(prev => {
          const exception = exceptions.find(e => e.id === exceptionId);
          const type = exception?.type || 'unmatched';
          return {
            ...prev,
            total: Math.max(0, prev.total - 1),
            [type]: Math.max(0, prev[type] - 1),
          };
        });
        onRefreshNeeded?.();
      }
    } catch {
      // silently fail
    }
  };

  if (!isOpen) return null;

  const filteredExceptions = exceptions.filter(e => e.type === activeTab);

  return (
    <div className="exceptions-modal-overlay" onClick={onClose}>
      <div className="exceptions-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
        {/* Header */}
        <div className="exceptions-modal-header">
          <div className="header-title-area">
            <AlertTriangle size={22} className="header-icon" />
            <h2>{t('contractExceptions.title', 'Contract Analysis Exceptions')}</h2>
          </div>
          <div className="header-actions">
            <button className="close-btn" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="exceptions-tabs">
          <button
            className={`exceptions-tab ${activeTab === 'unmatched' ? 'active' : ''}`}
            onClick={() => setActiveTab('unmatched')}
          >
            <FileQuestion size={18} />
            <span>{t('contractExceptions.unmatchedTab', 'Unmatched')}</span>
            {counts.unmatched > 0 && (
              <span className="tab-badge">{counts.unmatched}</span>
            )}
          </button>
          <button
            className={`exceptions-tab ${activeTab === 'ambiguous' ? 'active' : ''}`}
            onClick={() => setActiveTab('ambiguous')}
          >
            <Files size={18} />
            <span>{t('contractExceptions.ambiguousTab', 'Ambiguous')}</span>
            {counts.ambiguous > 0 && (
              <span className="tab-badge warning">{counts.ambiguous}</span>
            )}
          </button>
          <button
            className={`exceptions-tab ${activeTab === 'failure' ? 'active' : ''}`}
            onClick={() => setActiveTab('failure')}
          >
            <XCircle size={18} />
            <span>{t('contractExceptions.failureTab', 'Failures')}</span>
            {counts.failure > 0 && (
              <span className="tab-badge error">{counts.failure}</span>
            )}
          </button>
        </div>

        {/* Content */}
        <div className="exceptions-content">
          {loading ? (
            <div className="exceptions-loading">
              <Loader2 size={40} className="spinner-icon" />
              <p>{t('common.loading', 'Loading...')}</p>
            </div>
          ) : error ? (
            <div className="exceptions-error">
              <AlertTriangle size={40} />
              <p>{error}</p>
              <button onClick={loadExceptions}>{t('common.retry', 'Retry')}</button>
            </div>
          ) : filteredExceptions.length === 0 ? (
            <div className="exceptions-empty">
              <FileText size={56} strokeWidth={1} />
              <p>{t('contractExceptions.noExceptions', 'No exceptions in this category')}</p>
            </div>
          ) : (
            <div className="exceptions-list">
              {filteredExceptions.map((exception) => (
                <ContractExceptionItem
                  key={exception.id}
                  exception={exception}
                  onResolve={handleResolve}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Individual exception item
const ContractExceptionItem: React.FC<{
  exception: ContractException;
  onResolve: (id: string, resolution: string, tradeId?: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}> = ({ exception, onResolve, onDelete }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [linkTradeId, setLinkTradeId] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);

  const isBusy = resolving || deleting;

  const handleDismiss = async () => {
    setResolving(true);
    await onResolve(exception.id, 'dismissed');
    setResolving(false);
  };

  const handleLinkToTrade = async () => {
    if (!linkTradeId.trim()) return;
    setResolving(true);
    await onResolve(exception.id, 'linked', linkTradeId.trim());
    setResolving(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    await onDelete(exception.id);
    setDeleting(false);
    setShowDeleteConfirm(false);
  };

  const formatDateTime = (dateStr?: string): string => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
      return dateStr;
    }
  };

  const typeLabel = exception.type === 'unmatched'
    ? t('contractExceptions.typeUnmatched', 'No BTN match found')
    : exception.type === 'ambiguous'
    ? t('contractExceptions.typeAmbiguous', 'Multiple BTN matches')
    : t('contractExceptions.typeFailure', 'Processing failed');

  return (
    <div className={`exception-card ${exception.type} ${isExpanded ? 'expanded' : 'collapsed'}`}>
      {/* Header */}
      <div className="exception-card-header clickable" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="header-left">
          <span className="expand-icon">
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
          <div className="email-info-compact">
            <div className="email-sender">
              <FileText size={14} />
              <span>{exception.attachmentName}</span>
            </div>
            {!isExpanded && (
              <span className="trades-count-badge">{typeLabel}</span>
            )}
            {!isExpanded && (exception.emailDate || exception.createdAt) && (
              <span className="email-date-badge">
                {formatDateTime(exception.emailDate || exception.createdAt)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <>
          <div style={{ padding: '0.75rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem', fontSize: '0.85rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <span style={{ color: 'var(--text-secondary)', minWidth: '100px' }}>{t('contractExceptions.type', 'Type')}:</span>
              <span style={{ color: 'var(--text-primary)' }}>{typeLabel}</span>
            </div>
            {exception.emailSender && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <span style={{ color: 'var(--text-secondary)', minWidth: '100px' }}>{t('contractExceptions.sender', 'Sender')}:</span>
                <span style={{ color: 'var(--text-primary)' }}>{exception.emailSender}</span>
              </div>
            )}
            {exception.emailSubject && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <span style={{ color: 'var(--text-secondary)', minWidth: '100px' }}>{t('contractExceptions.subject', 'Subject')}:</span>
                <span style={{ color: 'var(--text-primary)' }}>{exception.emailSubject}</span>
              </div>
            )}
            {exception.emailDate && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <span style={{ color: 'var(--text-secondary)', minWidth: '100px' }}>{t('contractExceptions.emailDate', 'Email date')}:</span>
                <span style={{ color: 'var(--text-primary)' }}>{formatDateTime(exception.emailDate)}</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <span style={{ color: 'var(--text-secondary)', minWidth: '100px' }}>{t('contractExceptions.attachment', 'Attachment')}:</span>
              <span style={{ color: 'var(--text-primary)' }}>{exception.attachmentName}</span>
            </div>
            {exception.createdAt && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <span style={{ color: 'var(--text-secondary)', minWidth: '100px' }}>{t('contractExceptions.processedAt', 'Processed at')}:</span>
                <span style={{ color: 'var(--text-primary)' }}>{formatDateTime(exception.createdAt)}</span>
              </div>
            )}
            {exception.emailId && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <span style={{ color: 'var(--text-secondary)', minWidth: '100px' }}>{t('contractExceptions.emailId', 'Email ID')}:</span>
                <span style={{ color: 'var(--text-primary)', fontFamily: 'monospace', fontSize: '0.8rem' }}>{exception.emailId}</span>
              </div>
            )}
            {exception.retryCount != null && exception.retryCount > 0 && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <span style={{ color: 'var(--text-secondary)', minWidth: '100px' }}>{t('contractExceptions.retries', 'Retries')}:</span>
                <span style={{ color: 'var(--text-primary)' }}>{exception.retryCount}</span>
              </div>
            )}
            {exception.attemptedBtns && exception.attemptedBtns.length > 0 && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <span style={{ color: 'var(--text-secondary)', minWidth: '100px' }}>{t('contractExceptions.candidates', 'BTNs checked')}:</span>
                <span style={{ color: 'var(--text-primary)' }}>{exception.attemptedBtns.join(', ')}</span>
              </div>
            )}
            {exception.matchedTradeIds && exception.matchedTradeIds.length > 0 && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <span style={{ color: 'var(--text-secondary)', minWidth: '100px' }}>{t('contractExceptions.matchedTrades', 'Matched trades')}:</span>
                <span style={{ color: 'var(--text-primary)' }}>{exception.matchedTradeIds.length}</span>
              </div>
            )}
            {exception.errorMessage && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <span style={{ color: 'var(--text-secondary)', minWidth: '100px' }}>{t('contractExceptions.error', 'Error')}:</span>
                <span style={{ color: '#ef4444' }}>{exception.errorMessage}</span>
              </div>
            )}
          </div>

          {/* Link to trade input (for unmatched/ambiguous) */}
          {showLinkInput && (
            <div style={{ padding: '0 1rem 0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="text"
                placeholder={t('contractExceptions.tradeIdPlaceholder', 'Enter trade ID...')}
                value={linkTradeId}
                onChange={(e) => setLinkTradeId(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                style={{
                  flex: 1,
                  padding: '0.4rem 0.6rem',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '4px',
                  color: 'var(--text-primary)',
                  fontSize: '0.825rem',
                }}
              />
              <button
                className="reprocess-btn"
                onClick={handleLinkToTrade}
                disabled={!linkTradeId.trim() || isBusy}
                style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }}
              >
                {resolving ? <Loader2 size={14} className="spinner-icon" /> : <Check size={14} />}
                <span>{t('contractExceptions.link', 'Link')}</span>
              </button>
              <button
                className="dismiss-btn"
                onClick={() => { setShowLinkInput(false); setLinkTradeId(''); }}
                style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }}
              >
                <span>{t('common.cancel', 'Cancel')}</span>
              </button>
            </div>
          )}

          {/* Actions */}
          {!showLinkInput && (
            <div className="exception-card-footer">
              {exception.type !== 'failure' && (
                <button
                  className="reprocess-btn"
                  onClick={(e) => { e.stopPropagation(); setShowLinkInput(true); }}
                  disabled={isBusy}
                >
                  <Link2 size={16} />
                  <span>{t('contractExceptions.linkToTrade', 'Link to Trade')}</span>
                </button>
              )}
              <button
                className="dismiss-btn"
                onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
                disabled={isBusy}
              >
                {resolving ? <Loader2 size={16} className="spinner-icon" /> : <Check size={16} />}
                <span>{t('contractExceptions.dismiss', 'Dismiss')}</span>
              </button>
              <button
                className="dismiss-btn"
                onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(true); }}
                disabled={isBusy}
                style={{ color: '#ef4444' }}
              >
                {deleting ? <Loader2 size={16} className="spinner-icon" /> : <Trash2 size={16} />}
                <span>{t('contractExceptions.delete', 'Delete')}</span>
              </button>
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title={t('contractExceptions.deleteConfirmTitle', 'Delete Exception')}
        message={t('contractExceptions.deleteConfirmMessage', 'Are you sure you want to permanently delete this exception?')}
        confirmLabel={t('contractExceptions.delete', 'Delete')}
        cancelLabel={t('common.cancel', 'Cancel')}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        type="danger"
      />
    </div>
  );
};

export default ContractExceptionsModal;
